import { ApolloClient, InMemoryCache, from, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import fetch from 'cross-fetch';
import { GraphQLFormattedError } from 'graphql';

import { getIdToken } from '../utilities/api';
import { DEACTIVATED_ACCESS_QUERY_PARAM, MAINTENANCE_QUERY_PARAM } from 'utilities/constants';
import { pageUrl } from 'utilities/pageUrl';

interface DecodedToken {
  iss?: string;
  nonce?: string;
  oid?: string;
}

export const isTokenExpired = (token: string) => {
  const hasIdToken = !!token && token !== 'null';

  if (!hasIdToken) return false;

  const payload = token.split('.')[1];
  const payloadDecoded = JSON.parse(atob(payload));
  if (import.meta.env.VITE_APP_ENV !== 'production' && isDevMedtronicToken(payloadDecoded))
    return false;
  return new Date().getTime() > payloadDecoded.exp * 1000;
};

const isDevMedtronicToken = (payloadDecoded: DecodedToken) => {
  return (
    payloadDecoded.iss ===
      'https://login.microsoftonline.com/0a29d274-1367-4a8f-99c5-90c3dc7d4043/v2.0' &&
    payloadDecoded.nonce === '885fcdcc-2b3b-4579-b987-5399a5a6cb63' &&
    payloadDecoded.oid === '728bf4ee-11d8-439f-9e8d-ff970e862ab7'
  );
};

const shouldLogout = (graphQLError: GraphQLFormattedError, isTokenExpired: boolean) => {
  const errorMessage = graphQLError.message.toLowerCase();

  if (errorMessage === MAINTENANCE_QUERY_PARAM) return true;

  if (errorMessage === DEACTIVATED_ACCESS_QUERY_PARAM) return true;

  if (errorMessage === 'unauthorized' || isTokenExpired) return true;

  return false;
};

const isOnScreenRequiringAuthenticatedUser = () => {
  const location = window.location;
  // some steps of booking process under /create-account are protected by authentication
  // and are an exception to the /create-account rule
  if (location.pathname === pageUrl.createAccount()) {
    const searchParams = new URLSearchParams(location.search);
    const stepValue = searchParams.get('step') ?? '';
    const authenticationProtectedSteps = ['insurance', 'confirm', 'success'];
    const isOnProtectedStep = authenticationProtectedSteps.includes(stepValue);
    return isOnProtectedStep;
  }

  return (
    location.pathname !== pageUrl.createAccount() &&
    location.pathname !== pageUrl.login() &&
    location.pathname !== pageUrl.logout() &&
    location.pathname !== pageUrl.passwordReset()
  );
};

const onClientError = (errors: readonly GraphQLFormattedError[] | undefined) => {
  if (!errors) return;
  if (!isOnScreenRequiringAuthenticatedUser()) return;

  const { location } = window;

  for (const graphQLError of errors) {
    const idToken = ''; //graphQLError.operation?.getContext().headers?.idtoken;
    if (shouldLogout(graphQLError, isTokenExpired(idToken))) {
      // Check if user is on auth screen (Sign-in or Sign-up screen) exists, because we
      // don't want to redirect unauthenticated users to logout. There is no purpose in
      // trying to logout them and we don't want to put them in a loop looking like this:
      // signin/signup -> logout -> signin/signup
      if (graphQLError.message.toLowerCase() === MAINTENANCE_QUERY_PARAM) {
        return (location.href = pageUrl.logout({ maintenance: true }));
      }

      if (graphQLError.message.toLowerCase() === DEACTIVATED_ACCESS_QUERY_PARAM) {
        return (location.href = pageUrl.logout({ deactivatedAccess: true }));
      }

      const targetValue = encodeURIComponent(`${location.pathname}${location.search}`);

      const url = new URL(location.href);
      const toastType = url.searchParams.get('toast_type');
      const toastMessage = url.searchParams.get('toast_message');

      const logoutUrl = new URL(pageUrl.logout(), location.origin);
      logoutUrl.searchParams.set('target', targetValue);
      logoutUrl.searchParams.set('loggedOut', '1');

      if (toastType) logoutUrl.searchParams.set('toast_type', toastType);
      if (toastMessage) logoutUrl.searchParams.set('toast_message', toastMessage);

      return (location.href = logoutUrl.toString());
    }
  }
};

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      idtoken: getIdToken()
    }
  };
});

const isMaintenanceNetworkError = (networkError: unknown): boolean => {
  if (!networkError || typeof networkError !== 'object') return false;

  const error = networkError as { statusCode?: number; response?: { status?: number } };
  return error.statusCode === 503 || error.response?.status === 503;
};

const handleMaintenanceMode = () => {
  // Skip if already on logout page to avoid redirect loop
  if (window.location.pathname === pageUrl.logout()) return;

  window.location.href = pageUrl.logout({ maintenance: true });
};

const isProduction = () => import.meta.env.VITE_APP_ENV === 'production';

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // Handle 503 maintenance mode (non-production only)
  if (!isProduction() && isMaintenanceNetworkError(networkError)) {
    handleMaintenanceMode();
    return;
  }

  if (graphQLErrors) {
    onClientError(graphQLErrors);
  }
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_APP_APOLLO_URL,
  fetch: fetch
});

const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 5000, // if we didn't get response in 5s, stop retrying
    jitter: true // this will result with random next request in interval [initial, 2x initial]
  },
  attempts: {
    max: 10,
    retryIf: (error, _operation) => {
      // Don't retry 413 (payload too large) or 503 (maintenance mode)
      return error.statusCode !== 413 && error.statusCode !== 503;
    }
  }
});

const appLink = from([retryLink, errorLink, httpLink]);

const client = new ApolloClient<unknown>({
  link: authLink.concat(appLink),
  cache: new InMemoryCache()
});

client.defaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network'
  }
};

export default client;
